import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import Pusher from 'pusher-js';
import secureLocalStorage from 'react-secure-storage';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import * as Actions from '@/app/store/actions';
import ReportsHeader from '@shared/Reports/ReportsHeader';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import { v4 as uuidv4 } from 'uuid';
import { getRoyaltyPaymentReport, updateRoyaltyPaymentStatus } from '@/app/services/reports/reports';
import RoyaltyPaymentFiltersBar from './RoyaltyPaymentFiltersBar';
import RoyaltyPaymentTable from './RoyaltyPaymentTable';
import RoyaltyPaymentSelectionBar from './RoyaltyPaymentSelectionBar';
import RoyaltyPaymentPrintViewTable from './RoyaltyPaymentPrintViewTable';
import RoyaltyPaymentActionModal from './RoyaltyPaymentActionModal';
import { useNavigate } from '@/app/navigation';

const RoyaltyPaymentReport = () => {
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
	const navigate = useNavigate();

	const [filters, setFilters] = useState({
		page: 1,
		year: { value: currentYear, label: currentYear.toString() },
		quarter: { value: currentQuarter, label: `Q${currentQuarter}` },
		school_id: [],
		company_id: [],
		status: { value: 'all', label: 'All' },
		sort: 'school',
		dir: 'asc',
	});

	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const [rowsPerPage] = useState(20);
	const [selectedIds, setSelectedIds] = useState([]);
	const [loading, setLoading] = useState(false);
	const [printData, setPrintData] = useState([]);
	const [printDataLoading, setPrintDataLoading] = useState(false);
	const [exportLoading, setExportLoading] = useState(false);
	const [exportType, setExportType] = useState(null);
	const printRef = useRef();
	const dispatch = useDispatch();
	const [actionModalOpen, setActionModalOpen] = useState(false);
	const [actionTargetStatus, setActionTargetStatus] = useState(null);
	const user = useSelector((state) => state.auth.user);
	const viewAsId = secureLocalStorage.getItem('view_as_id');
	const authToken = secureLocalStorage.getItem('jwt_access_token');
	const exportTimeoutRef = useRef(null);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	const generateQueryParams = (filts, exp = null) => {
		return {
			page: filts.page,
			year: filts.year?.value ?? null,
			quarter: filts.quarter?.value ?? null,
			school_id: filts.school_id?.length ? filts.school_id.map((s) => s.value) : null,
			company_id: filts.company_id?.length ? filts.company_id.map((c) => c.value) : null,
			status: filts.status?.value && filts.status.value !== 'all' ? filts.status.value : null,
			sort: filts.sort,
			dir: filts.dir,
			export: exp,
		};
	};

	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some(
			(key) => key !== 'page' && newFilters[key] !== filters[key]
		);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};

	useEffect(() => {
		setSelectedIds([]);
		// Only fetch when required filters are present; otherwise clear results
		if (filters.year?.value && filters.quarter?.value) {
			const params = generateQueryParams(filters);
			setLoading(true);
			getRoyaltyPaymentReport(params)
				.then((res) => {
					const data = res?.data?.data || [];
					setRows(data);
					setTotalResults(res?.data?.meta?.total || data.length || 0);
				})
				.catch(() => {
					dispatch(
						Actions.showMessage({ message: 'Failed to fetch royalty payment report', variant: 'error' })
					);
					setRows([]);
					setTotalResults(0);
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			setRows([]);
			setTotalResults(0);
		}
	}, [
		filters.year,
		filters.quarter,
		filters.school_id,
		filters.company_id,
		filters.status,
		filters.page,
		filters.sort,
		filters.dir,
	]);

	const handleExport = async (type = 'csv') => {
		try {
			const params = generateQueryParams(filters, type);
			setExportLoading(true);
			params.uuid = uuidv4();
			window.localStorage.setItem('export_uuid', JSON.stringify(params.uuid));
			await getRoyaltyPaymentReport(params);
			// safety timeout in case no pusher event comes through
			exportTimeoutRef.current = setTimeout(() => {
				if (!exportLoading) return;
				setExportLoading(false);
				dispatch(
					Actions.showMessage({
						message: 'Export failed to generate. Please try again.',
						variant: 'error',
					})
				);
			}, 20000);
		} catch (_) {
			setExportLoading(false);
			dispatch(Actions.showMessage({ message: 'Failed to export report', variant: 'error' }));
		}
	};

	const handlePrintClick = async () => {
		if (!filters.year?.value || !filters.quarter?.value) return;
		setExportType('print');
		setPrintDataLoading(true);
		setPrintData([]);
		try {
			const params = generateQueryParams(filters, 'print');
			const res = await getRoyaltyPaymentReport(params);
			const data = res?.data?.data || [];
			setPrintData(data);
		} catch (e) {
			// no-op
		} finally {
			setPrintDataLoading(false);
		}
	};

	// After print data is ready and rendered, trigger print/PDF
	useEffect(() => {
		if (!printData?.length) return;
		if (exportType === 'print' && !printDataLoading) {
			handlePrint();
		}
	}, [exportType, printData, printDataLoading]);

	// Pusher subscription for export completion
	useEffect(() => {
		// wait until auth and user are ready
		if (!authToken || !user || !user.role || (!user.data?.id && !viewAsId)) {
			return;
		}
		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			channelAuthorization: {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				endpoint: `${import.meta.env.VITE_API_ENDPOINT}broadcasting/auth`,
			},
		});
		const userId = user.role[0] === 'super_school_admin' ? viewAsId : user.data?.id;
		if (!userId) {
			pusher.disconnect();
			return;
		}
		const channel = pusher.subscribe(`private-App.Models.User.${userId}`);

		channel.bind('report.export.complete', (res) => {
			const exportLink = JSON.parse(window.localStorage.getItem('export_uuid'));
			if (exportLink && exportLink === res.uuid) {
				setExportLoading(false);
				if (exportTimeoutRef.current) {
					clearTimeout(exportTimeoutRef.current);
				}
				window.localStorage.removeItem('export_uuid');

				const anchor = document.createElement('a');
				anchor.href = res.link;
				anchor.download = res.filename;
				anchor.click();
				anchor.remove();
			}
		});

		return () => {
			pusher.disconnect();
		};
	}, [authToken, user, viewAsId]);

	const selectionState = useMemo(() => {
		if (!selectedIds.length) {
			return {
				canMarkPaid: false,
				canMarkUnpaid: false,
				paidDisabledReason: undefined,
				unpaidDisabledReason: undefined,
			};
		}
		const selectedRows = rows.filter((r) => selectedIds.includes(r.school?.id));
		const anyUnqualified = selectedRows.some((r) => !r.qualified);
		const hasPaid = selectedRows.some((r) => r.status === 'paid');
		const hasUnpaid = selectedRows.some((r) => r.status === 'unpaid');
		const mixedStatus = hasPaid && hasUnpaid;
		const allPaid = selectedRows.every((r) => r.status === 'paid');
		const allUnpaid = selectedRows.every((r) => r.status === 'unpaid');

		let paidDisabledReason;
		let unpaidDisabledReason;

		if (anyUnqualified) {
			paidDisabledReason = 'Some selected schools are not qualified';
			unpaidDisabledReason = 'Some selected schools are not qualified';
		} else if (mixedStatus) {
			paidDisabledReason = 'Selection contains both paid and unpaid statuses';
			unpaidDisabledReason = 'Selection contains both paid and unpaid statuses';
		} else {
			if (allPaid) paidDisabledReason = 'All selected schools are already paid';
			if (allUnpaid) unpaidDisabledReason = 'All selected schools are already unpaid';
		}

		const canMarkPaid = !anyUnqualified && !mixedStatus && !allPaid;
		const canMarkUnpaid = !anyUnqualified && !mixedStatus && !allUnpaid;

		return { canMarkPaid, canMarkUnpaid, paidDisabledReason, unpaidDisabledReason };
	}, [selectedIds, rows]);

	const bulkUpdateStatus = async (status) => {
		try {
			const payload = {
				year: filters.year.value,
				quarter: filters.quarter.value,
				school_id: selectedIds,
				status,
			};
			await updateRoyaltyPaymentStatus(payload);
			dispatch(Actions.showMessage({ message: 'Payment status updated', variant: 'success' }));
			// Targeted refresh: fetch just the updated rows and merge into current table
			try {
				const params = generateQueryParams(filters);
				params.school_id = selectedIds;
				params.page = 1; // ensure we get results for specified school ids
				const res = await getRoyaltyPaymentReport(params);
				const updated = res?.data?.data || [];
				const updatedById = new Map(updated.map((r) => [r?.school?.id, r]));
				setRows((prev) => {
					let next = prev.map((r) =>
						selectedIds.includes(r?.school?.id) ? updatedById.get(r?.school?.id) || r : r
					);
					// If a status filter is active, remove rows that no longer match
					const statusFilter = filters.status?.value;
					if (statusFilter && statusFilter !== 'all') {
						next = next.filter((r) => r?.status === statusFilter);
					}
					return next;
				});
			} catch (_) {
				// fallback: optimistically update local rows if fetch fails
				setRows((prev) => {
					let next = prev.map((r) =>
						selectedIds.includes(r?.school?.id)
							? { ...r, status, payment_date: status === 'paid' ? new Date().toISOString() : null }
							: r
					);
					const statusFilter = filters.status?.value;
					if (statusFilter && statusFilter !== 'all') {
						next = next.filter((r) => r?.status === statusFilter);
					}
					return next;
				});
			}
			setSelectedIds([]);
		} catch (e) {
			dispatch(Actions.showMessage({ message: 'Failed to update status', variant: 'error' }));
		}
	};

	const openConfirmModal = (status) => {
		setActionTargetStatus(status);
		setActionModalOpen(true);
	};

	const handleConfirmAction = async () => {
		const status = actionTargetStatus;
		setActionModalOpen(false);
		if (!status) return;
		await bulkUpdateStatus(status);
		setActionTargetStatus(null);
	};

	// columns config similar to admin RoyaltyTable
	const currency = (v) =>
		typeof v === 'number' ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : (v ?? '-');

	const columns = [
		{
			id: 'school',
			label: 'School Name',
			widthClass: 'min-w-200',
			render: (row) => row.school?.name ?? row.company?.name ?? '-',
		},
		{
			id: 'total_families',
			label: 'Total Families',
			widthClass: 'min-w-96',
			render: (row) => row.total_families ?? 0,
		},
		{
			id: 'qualification',
			label: 'Qualification %',
			widthClass: 'min-w-96',
			render: (row) => {
				const isQualified = row.qualified;
				return (
					<span className={`font-bold text-12 ${isQualified ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
						{`${(row.qualification * 100).toFixed(0)}%`}
					</span>
				);
			},
		},
		{
			id: 'paid_families',
			label: 'Paid Families',
			widthClass: 'min-w-96',
			render: (row) => row.paid_families ?? 0,
		},
		{
			id: 'total_school_royalty',
			label: 'Total Royalty',
			widthClass: 'min-w-128',
			render: (row) => currency(row.total_school_royalty ?? 0),
		},
		{
			id: 'status',
			label: 'Payment Status',
			widthClass: 'min-w-96',
			render: (row) => {
				const getStyles = (status) => {
					switch (status) {
						case 'paid':
							return (
								<div className="bg-[#10B981] text-white rounded-full px-4 py-4 flex items-center justify-center text-12">
									Paid
								</div>
							);
						case 'unpaid':
						case 'pending':
							return (
								<div className="bg-[#F59E0B] text-white rounded-full px-4 py-4 flex items-center justify-center text-12">
									Unpaid
								</div>
							);
						default:
							return (
								<div className="bg-[#EF4444] text-white rounded-full px-4 py-4 flex items-center justify-center text-12">
									Not Qualified
								</div>
							);
					}
				};
				return getStyles(row.status);
			},
		},
		{
			id: 'payment_date',
			label: 'Date Paid',
			widthClass: 'min-w-96',
			render: (row) => (row.payment_date ? new Date(row.payment_date).toLocaleDateString() : '—'),
		},
	];

	return (
		<div className="bg-white">
			<ReportsHeader
				onBack={() => navigate(-1)}
				onExport={handleExport}
				onPrint={handlePrintClick}
				onDownloadPDF={() => handleExport('pdf')}
				title="Royalty Payment Report"
				subtitle="Track royalty amounts for each quarter for your school."
				exportsDisabled={!rows?.length}
				exportLoading={exportLoading}
				printLoading={printDataLoading}
				pdfLoading={false}
			/>

			<RoyaltyPaymentFiltersBar filters={filters} setFilters={updateFilters} />
			<AppliedFiltersBar filters={filters} setFilters={updateFilters} hideFilters={['page', 'sort', 'dir']} />
			<RoyaltyPaymentTable
				columns={columns}
				data={rows}
				filters={filters}
				page={filters.page}
				rowsPerPage={rowsPerPage}
				setPage={(page) => updateFilters({ ...filters, page })}
				totalResults={totalResults}
				setFilters={updateFilters}
				selectedIds={selectedIds}
				setSelectedIds={setSelectedIds}
				loading={loading}
			/>
			<RoyaltyPaymentSelectionBar
				selectedCount={selectedIds.length}
				canMarkPaid={selectionState.canMarkPaid}
				canMarkUnpaid={selectionState.canMarkUnpaid}
				paidDisabledReason={selectionState.paidDisabledReason}
				unpaidDisabledReason={selectionState.unpaidDisabledReason}
				onMarkPaid={() => openConfirmModal('paid')}
				onMarkUnpaid={() => openConfirmModal('unpaid')}
			/>
			<RoyaltyPaymentActionModal
				open={actionModalOpen}
				onClose={() => setActionModalOpen(false)}
				targetStatus={actionTargetStatus}
				selectedRows={rows.filter((r) => selectedIds.includes(r.school?.id))}
				year={filters.year?.value}
				quarter={filters.quarter?.value}
				onConfirm={handleConfirmAction}
			/>
			<div className="h-0 overflow-hidden">
				<RoyaltyPaymentPrintViewTable
					columns={columns}
					data={printData}
					exportType={exportType}
					filters={filters}
					ref={printRef}
					title="Royalty Payment Report"
					user={user}
				/>
			</div>
		</div>
	);
};

export default RoyaltyPaymentReport;
